/**
 * DashboardChart — Phase-10 Uebersichts-Chart fuer MainPage.
 *
 * Zweiteilig:
 *   LINKS  Donut (PieChart) — tracking_status-Verteilung (Paket-Tab) oder Lieferanten-Verteilung (Lieferanten-Tab)
 *   RECHTS Mini-Balken (BarChart layout=vertical) — DHL-internal_status-Verteilung (nur Paket-Tab)
 *                                                 — Platzhalter-Text "Status-Verlauf siehe Drawer" auf Lieferanten-Tab
 *
 * Beide Charts senden Click-Events nach oben via onSegmentClick + onBarClick.
 * Pure presentational Component — kein useState, kein fetch, kein effect.
 * Recharts 3.8.1 (in root node_modules ueber npm workspace hoisting).
 *
 * Phase 11: Tab-konditionales Rendering via kategorie === 'lieferanten'.
 *
 * CI-Farben eingehalten:
 *   - Tooltip-Border: #DAD9D9
 *   - Text-Color: #1A1A1A
 *   - Carrier-Segment: #E9FF57 (CI-Gelb)
 *   - Keine weisse Schrift auf gelbem Hintergrund
 */

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { PieSectorDataItem } from 'recharts/types/polar/Pie';
import type { BarRectangleItem } from 'recharts/types/cartesian/Bar';
import type { SectionId, VersandKategorie } from '../lib/sectionQueryBuilder';
import {
  mapStatsToDonutData,
  mapStatsToBarData,
  mapStatsToSupplierData,
  type DashboardStatsResponse,
  type InternalStatusKey,
} from '../lib/dashboardStatsMapper';

// Re-export types so MainPage kann direkt aus diesem Module importieren
export type { DashboardStatsResponse, InternalStatusKey };

export interface DashboardChartProps {
  stats: DashboardStatsResponse | null;
  kategorie: VersandKategorie;
  loading?: boolean;
  onSegmentClick: (sectionId: SectionId) => void;
  onBarClick: (dhlStatus: InternalStatusKey) => void;
}

// Tooltip-Stil CI-konform
const TOOLTIP_STYLE = {
  borderColor: '#DAD9D9',
  borderRadius: '6px',
  fontSize: 12,
  backgroundColor: 'white',
  color: '#1A1A1A',
} as const;

export default function DashboardChart({
  stats,
  kategorie,
  loading,
  onSegmentClick,
  onBarClick,
}: DashboardChartProps) {
  // Loading-State — nur wenn noch keine Daten
  if (loading && !stats) {
    return (
      <div className="border border-[#DAD9D9] rounded-lg p-4 bg-white">
        <div className="text-sm text-gray-500 italic text-center py-8">
          Lade Uebersicht…
        </div>
      </div>
    );
  }

  // Kein Stats-Objekt — nichts rendern
  if (!stats) {
    return null;
  }

  // Phase 11: Tab-konditional — Lieferanten-Tab zeigt Lieferanten-Verteilung, Paket-Tab tracking_status
  const donutData = kategorie === 'lieferanten'
    ? mapStatsToSupplierData(stats)
    : mapStatsToDonutData(stats);

  const barData = mapStatsToBarData(stats);
  const totalDonut = donutData.reduce((sum, d) => sum + d.value, 0);
  const totalBar = barData.reduce((sum, b) => sum + b.value, 0);

  return (
    <div className="border border-[#DAD9D9] rounded-lg p-4 bg-white mb-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* LINKS: Donut — Tab-konditional: tracking_status (Paket) oder Lieferanten-Verteilung */}
        <div>
          <div className="text-xs font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
            {kategorie === 'lieferanten' ? 'Lieferanten-Verteilung' : 'Auftrags-Status'}
          </div>

          {totalDonut === 0 ? (
            <div className="text-sm text-gray-500 italic text-center py-12">
              Keine Auftraege
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  cursor="pointer"
                  onClick={(entry: PieSectorDataItem & { sectionId?: SectionId }) => {
                    if (entry && entry.sectionId) {
                      onSegmentClick(entry.sectionId);
                    }
                  }}
                >
                  {donutData.map((entry) => (
                    <Cell
                      key={entry.sectionId}
                      fill={entry.color}
                      stroke="#FFFFFF"
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formatter={(value: any) => value.toLocaleString('de-DE')}
                />
              </PieChart>
            </ResponsiveContainer>
          )}

          {/* Legende unter dem Donut — klickbar */}
          {totalDonut > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-[#1A1A1A]">
              {donutData.map((entry) => (
                <button
                  key={entry.sectionId}
                  type="button"
                  onClick={() => onSegmentClick(entry.sectionId)}
                  className="flex items-center gap-1 hover:underline focus:outline-none"
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ background: entry.color }}
                  />
                  <span>{entry.label}:</span>
                  <span className="font-semibold">
                    {entry.value.toLocaleString('de-DE')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RECHTS: Mini-Balken — DHL internal_status (Paket-Tab) oder Platzhalter (Lieferanten-Tab) */}
        <div>
          <div className="text-xs font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
            {kategorie === 'lieferanten' ? 'Lieferanten-Detail' : 'DHL-Status (Carrier-Orders)'}
          </div>

          {/* Phase 11: Lieferanten-Tab — kein DHL-Tracking relevant */}
          {kategorie === 'lieferanten' ? (
            <div className="text-sm text-gray-500 italic text-center py-12">
              Status-Verlauf siehe Drawer
            </div>
          ) : totalBar === 0 ? (
            <div className="text-sm text-gray-500 italic text-center py-12">
              Keine Carrier-Orders
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={barData}
                margin={{ top: 5, right: 16, left: 0, bottom: 0 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={110}
                  tick={{ fontSize: 11, fill: '#1A1A1A' }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formatter={(value: any) => value.toLocaleString('de-DE')}
                />
                <Bar
                  dataKey="value"
                  cursor="pointer"
                  onClick={(entry: BarRectangleItem & { dhlStatus?: InternalStatusKey }) => {
                    if (entry && entry.dhlStatus) {
                      onBarClick(entry.dhlStatus);
                    }
                  }}
                >
                  {barData.map((entry) => (
                    <Cell
                      key={entry.dhlStatus}
                      fill={entry.color}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

      </div>
    </div>
  );
}
